import React, { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import EmailCaptureModal from '../components/EmailCaptureModal';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileSpreadsheet, 
  Upload,
  Loader2,
  AlertTriangle,
  DollarSign,
  Users,
  Mail,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  ArrowRight,
  Stethoscope,
  Activity,
  Phone,
  Database,
  Copy,
  FileCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Donut Chart Component for 4 Pillars
const PillarDonutChart = ({ pillarScores, globalScore, status }) => {
  const pillars = [
    { key: 'email_deliverability', color: '#ef4444', label: 'Email' },
    { key: 'data_completeness', color: '#3b82f6', label: 'Completeness' },
    { key: 'duplicates_consistency', color: '#8b5cf6', label: 'Duplicates' },
    { key: 'standardization', color: '#f59e0b', label: 'Formatting' }
  ];

  const getStatusColor = () => {
    if (status === 'critical') return '#ef4444';
    if (status === 'moderate') return '#f97316';
    return '#22c55e';
  };

  const getStatusLabel = () => {
    if (status === 'critical') return 'CRITICAL';
    if (status === 'moderate') return 'MODERATE RISK';
    return 'OPTIMAL';
  };

  // Calculate arc segments
  const size = 200;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  // Build segments based on available pillars with weights
  const activePillars = pillars.filter(p => pillarScores[p.key]?.available);
  let currentOffset = 0;

  return (
    <div className="flex flex-col items-center">
      {/* Donut Chart */}
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth={strokeWidth}
          />
          
          {/* Colored segments */}
          {activePillars.map((pillar, idx) => {
            const data = pillarScores[pillar.key];
            const weight = data?.weight || 25;
            const score = data?.score || 0;
            
            // Segment length based on weight
            const segmentLength = (weight / 100) * circumference;
            const offset = currentOffset;
            currentOffset += segmentLength;
            
            // Color intensity based on score
            const opacity = Math.max(0.3, score / 100);
            
            return (
              <circle
                key={pillar.key}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={pillar.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
                strokeDashoffset={-offset}
                style={{ opacity }}
              />
            );
          })}
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-4xl font-bold" style={{ color: getStatusColor() }}>
            {globalScore}
          </div>
          <div 
            className="text-xs font-bold tracking-wider mt-1 px-2 py-0.5 rounded-full"
            style={{ 
              backgroundColor: `${getStatusColor()}20`,
              color: getStatusColor()
            }}
          >
            {getStatusLabel()}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-3 mt-6 w-full max-w-xs">
        {activePillars.map(pillar => {
          const data = pillarScores[pillar.key];
          return (
            <div key={pillar.key} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: pillar.color }}
              />
              <div className="text-xs">
                <span className="text-slate-600">{pillar.label}</span>
                <span className="font-semibold text-slate-900 ml-1">
                  {Math.round(data?.score || 0)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Mini Pillar Bar Component
const PillarBar = ({ label, score, weight, icon: Icon, color, bgColor, description }) => {
  const getScoreColor = (score) => {
    if (score < 60) return 'bg-red-500';
    if (score < 85) return 'bg-orange-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-slate-900 text-sm">{label}</h4>
            <span className="text-lg font-bold text-slate-900">{Math.round(score)}%</span>
          </div>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getScoreColor(score)} transition-all duration-500`}
          style={{ width: `${score}%` }}
        />
      </div>
      
      <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
        <span>Weight: {Math.round(weight)}%</span>
        <span className={score < 60 ? 'text-red-600' : score < 85 ? 'text-orange-600' : 'text-emerald-600'}>
          {score < 60 ? 'Critical' : score < 85 ? 'Needs Work' : 'Healthy'}
        </span>
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    critical: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: '🚨 CRITICAL' },
    warning: { icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-100', label: '⚠️ WARNING' },
    duplicate: { icon: Users, color: 'text-purple-600', bg: 'bg-purple-100', label: '💀 DUPLICATE' },
    format: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-100', label: '❌ FORMAT' },
  };
  
  const { color, bg, label } = config[status] || config.warning;
  
  return (
    <span className={`${bg} ${color} px-2 py-1 rounded text-xs font-medium`}>
      {label}
    </span>
  );
};

const CRMDoctorPage = () => {
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [downloadType, setDownloadType] = useState(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File size must be under 50MB');
      return;
    }
    
    setFile(selectedFile);
    setReport(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const analyzeFile = async () => {
    if (!file) return;
    
    setAnalyzing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/tools/csv-doctor/analyze`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        }
      );
      
      setReport(response.data);
      
    } catch (error) {
      console.error('Analysis error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to analyze file';
      toast.error(errorMessage);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDownload = (type) => {
    // For logged-in users, download directly
    if (user?.email) {
      triggerDownload(type);
    } else {
      // For anonymous users, show email capture modal
      setDownloadType(type);
      setShowEmailModal(true);
    }
  };

  const triggerDownload = (type) => {
    const url = `${API_URL}/api/tools/csv-doctor/segment/${report.report_id}/${type}`;
    window.open(url, '_blank');
    toast.success('Download started!');
  };

  const handleEmailSuccess = (downloadUrl) => {
    if (downloadType) {
      triggerDownload(downloadType);
    }
    setShowEmailModal(false);
    setDownloadType(null);
  };

  const resetAnalysis = () => {
    setFile(null);
    setReport(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Shared Header */}
      <Header />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 sm:pb-12">
        {!report ? (
          /* Upload Section */
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full text-red-700 text-sm font-medium mb-4">
                <Stethoscope className="w-4 h-4" />
                Free CRM Audit
              </div>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
                Is Your CRM Data Bleeding Revenue?
              </h1>
              <p className="text-lg text-slate-600">
                Upload your contact list and discover how much money you&apos;re wasting on bad data.
              </p>
            </div>

            {/* Dropzone */}
            <div
              className={`border-2 border-dashed rounded-2xl p-8 sm:p-12 text-center transition-all cursor-pointer
                ${file ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'}`}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              data-testid="csv-doctor-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                data-testid="csv-doctor-file-input"
              />
              
              {file ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto">
                    <FileSpreadsheet className="w-8 h-8 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); resetAnalysis(); }}
                  >
                    Change file
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto">
                    <Upload className="w-8 h-8 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Drop your CSV or Excel file here</p>
                    <p className="text-sm text-slate-500">or click to browse (max 50MB)</p>
                  </div>
                </div>
              )}
            </div>

            {/* Analyze Button */}
            {file && (
              <Button
                onClick={analyzeFile}
                disabled={analyzing}
                className="w-full mt-6 h-14 text-lg bg-red-600 hover:bg-red-700 text-white"
                data-testid="csv-doctor-analyze-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Auditing Your Data...
                  </>
                ) : (
                  <>
                    <Activity className="w-5 h-5 mr-2" />
                    Run Free Data Audit
                  </>
                )}
              </Button>
            )}

            {/* Trust Badges */}
            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                <span>Secure</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                <span>No signup required</span>
              </div>
            </div>
          </div>
        ) : (
          /* Report Section */
          <div className="space-y-8">
            {/* Header with Score */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mb-2">
                    CRM Data Audit Report
                  </h1>
                  <p className="text-slate-600 mb-4">
                    {report.file_name} • {report.total_rows?.toLocaleString()} contacts analyzed
                  </p>
                  
                  {/* Status message based on score */}
                  <div className={`p-4 rounded-xl ${
                    report.status === 'critical' ? 'bg-red-50 border border-red-200' :
                    report.status === 'moderate' ? 'bg-orange-50 border border-orange-200' :
                    'bg-green-50 border border-green-200'
                  }`}>
                    <p className={`font-medium ${
                      report.status === 'critical' ? 'text-red-800' :
                      report.status === 'moderate' ? 'text-orange-800' :
                      'text-green-800'
                    }`}>
                      {report.status_copy}
                    </p>
                  </div>
                </div>
                
                {/* Donut Chart */}
                <div className="flex justify-center">
                  {report.pillar_scores && (
                    <PillarDonutChart 
                      pillarScores={report.pillar_scores}
                      globalScore={report.health_score}
                      status={report.status}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* 4 Pillar Scores */}
            {report.pillar_scores && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <PillarBar
                  label="Email Deliverability"
                  score={report.pillar_scores.email_deliverability?.score || 0}
                  weight={report.pillar_scores.email_deliverability?.weight || 0}
                  icon={Mail}
                  color="text-red-600"
                  bgColor="bg-red-100"
                  description="Valid email addresses"
                />
                <PillarBar
                  label="Data Completeness"
                  score={report.pillar_scores.data_completeness?.score || 0}
                  weight={report.pillar_scores.data_completeness?.weight || 0}
                  icon={Database}
                  color="text-blue-600"
                  bgColor="bg-blue-100"
                  description="Critical fields filled"
                />
                <PillarBar
                  label="Duplicates"
                  score={report.pillar_scores.duplicates_consistency?.score || 0}
                  weight={report.pillar_scores.duplicates_consistency?.weight || 0}
                  icon={Copy}
                  color="text-purple-600"
                  bgColor="bg-purple-100"
                  description="Unique records"
                />
                <PillarBar
                  label="Standardization"
                  score={report.pillar_scores.standardization?.score || 0}
                  weight={report.pillar_scores.standardization?.weight || 0}
                  icon={FileCheck}
                  color="text-amber-600"
                  bgColor="bg-amber-100"
                  description="Properly formatted"
                />
              </div>
            )}

            {/* Phone Formatter Promotion - Show when phone issues detected */}
            {(report.pillars?.data_hygiene?.bad_phone_numbers > 0) && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-emerald-900 mb-1">
                      {report.pillars.data_hygiene.bad_phone_numbers} phone numbers need formatting
                    </h3>
                    <p className="text-emerald-700 text-sm mb-3">
                      Fix your phone numbers instantly with our free Phone Formatter tool. 
                      Convert to E.164 format for HubSpot, Salesforce, and dialers.
                    </p>
                    <Link to="/tools/phone-formatter">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Phone className="w-4 h-4 mr-2" />
                        Format Phone Numbers Free
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Email Fixer Promotion - Show when email issues detected */}
            {(report.pillars?.email_health && 
              (report.pillars.email_health.invalid_syntax > 0 || 
               report.pillars.email_health.typo_domains > 0)) && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900 mb-1">
                      {(report.pillars.email_health.invalid_syntax || 0) + (report.pillars.email_health.typo_domains || 0)} emails need fixing
                    </h3>
                    <p className="text-blue-700 text-sm mb-3">
                      Fix typos like gmal.com → gmail.com and validate email formats instantly 
                      with our free Email Fixer tool. Improve your deliverability rate.
                    </p>
                    <Link to="/tools/email-formatter">
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                        <Mail className="w-4 h-4 mr-2" />
                        Fix Emails Free
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Table */}
            {report.preview_rows?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-slate-100">
                  <h2 className="text-xl font-semibold text-slate-900">Top Offenders Preview</h2>
                  <p className="text-slate-500 text-sm">These are the worst records in your file</p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Row</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Email</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Phone</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Error Detected</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {report.preview_rows?.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="px-4 py-3 text-slate-900 font-medium">#{row.row_number || i + 1}</td>
                          <td className="px-4 py-3 text-slate-600 font-mono text-xs">{row.email || 'N/A'}</td>
                          <td className="px-4 py-3 text-slate-600">{row.phone || 'N/A'}</td>
                          <td className="px-4 py-3 text-slate-500">{row.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                  <p className="text-slate-600 text-sm">
                    We found <span className="font-semibold text-red-600">{report.total_issues?.toLocaleString() || 0} issues</span> in your file.
                    Incorrect data like this leads to hard bounces and lower sender reputation.
                  </p>
                </div>
              </div>
            )}

            {/* Segmented Download Files */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Download Records by Category</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Download All Issues */}
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-purple-800">All Issues</span>
                  </div>
                  <p className="text-sm text-purple-700 mb-3">{report.total_issues || 0} issues found</p>
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload('all_issues')}
                    className="text-purple-600 hover:text-purple-800 hover:bg-purple-100 p-0"
                  >
                    <Download className="w-4 h-4 mr-1" /> Download CSV
                  </Button>
                </div>
                
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">No Mistakes</span>
                  </div>
                  <p className="text-sm text-green-700 mb-3">{report.segments?.clean_count || 0} clean records</p>
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload('clean')}
                    className="text-green-600 hover:text-green-800 hover:bg-green-100 p-0"
                  >
                    <Download className="w-4 h-4 mr-1" /> Download CSV
                  </Button>
                </div>
                
                <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-red-800">Email Issues</span>
                  </div>
                  <p className="text-sm text-red-700 mb-3">{report.segments?.email_issues_count || 0} records</p>
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload('email')}
                    className="text-red-600 hover:text-red-800 hover:bg-red-100 p-0"
                  >
                    <Download className="w-4 h-4 mr-1" /> Download CSV
                  </Button>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    <span className="font-semibold text-orange-800">Phone Issues</span>
                  </div>
                  <p className="text-sm text-orange-700 mb-3">{report.segments?.phone_issues_count || 0} records</p>
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload('phone')}
                    className="text-orange-600 hover:text-orange-800 hover:bg-orange-100 p-0"
                  >
                    <Download className="w-4 h-4 mr-1" /> Download CSV
                  </Button>
                </div>
              </div>
            </div>

            {/* Fix It Now - Free Tools Promo Cards */}
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Fix It Now</h2>
              <p className="text-slate-600 text-sm mb-4">Use our free tools to clean specific data issues</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Email Format Fixer Card */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-md transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 mb-1">Email Format Fixer</h3>
                      <p className="text-slate-600 text-sm mb-3">
                        Fix typos like gmal.com → gmail.com. Validate formats and improve deliverability.
                      </p>
                      <Link to="/tools/email-formatter">
                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                          Fix Emails Free <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Phone Formatter Card */}
                <div className="bg-white rounded-xl border border-slate-200 p-5 hover:border-emerald-300 hover:shadow-md transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 mb-1">Phone Formatter</h3>
                      <p className="text-slate-600 text-sm mb-3">
                        Convert to E.164 format for HubSpot, Salesforce, and dialers instantly.
                      </p>
                      <Link to="/tools/phone-formatter">
                        <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                          Format Phones Free <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA to Use CRM Data Cleaner */}
            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 sm:p-8 text-white text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                Use CRM Data Cleaner to Fix These Issues Within Minutes
              </h2>
              <p className="text-indigo-100 text-lg mb-6">
                First 100 rows are <span className="font-bold text-white">FREE</span> — no credit card required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/login">
                  <Button className="h-14 px-8 text-lg bg-white text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto">
                    Start Cleaning Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/pricing/subscription">
                  <Button variant="outline" className="h-14 px-8 text-lg border-white text-white hover:bg-white/10 w-full sm:w-auto">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>

            {/* Start Over */}
            <div className="text-center">
              <Button variant="ghost" onClick={resetAnalysis}>
                Analyze Another File
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Email Capture Modal for Anonymous Users */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setDownloadType(null);
        }}
        onSuccess={handleEmailSuccess}
        tool="csv-doctor"
        fileId={report?.report_id}
        toolName="CSV Doctor"
        accentColor="red"
      />
      
      <SupportButton />
    </div>
  );
};

export default CRMDoctorPage;
